import { showSnackbar } from '@/redux/snackbarSlice';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CakeRoundedIcon from '@mui/icons-material/CakeRounded';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import HourglassEmptyRoundedIcon from '@mui/icons-material/HourglassEmptyRounded';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import PlayCircleFilledRoundedIcon from '@mui/icons-material/PlayCircleFilledRounded';
import RedoRoundedIcon from '@mui/icons-material/RedoRounded';
import ThumbUpAltRoundedIcon from '@mui/icons-material/ThumbUpAltRounded';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Menu,
  MenuItem,
} from '@mui/material';
import { alpha, styled } from '@mui/material/styles';
import { useState } from 'react';
import { useDispatch } from 'react-redux';

import {
  usePutActivateStateCodeMutation,
  usePutPauseStateCodeMutation,
  usePutResetInitializationStateCodeMutation,
  usePutResumeStateCodeMutation,
} from '@/api/subscription-manager/stateCodeApiSlice';

// --- Styled Menu copied from your example ---
const StyledMenu = styled((props) => (
  <Menu
    elevation={0}
    anchorOrigin={{
      vertical: 'bottom',
      horizontal: 'right',
    }}
    transformOrigin={{
      vertical: 'top',
      horizontal: 'right',
    }}
    {...props}
  />
))(({ theme }) => ({
  '& .MuiPaper-root': {
    borderRadius: 6,
    marginTop: theme.spacing(1),
    minWidth: 180,
    color: 'rgb(55, 65, 81)',
    boxShadow:
      'rgb(255, 255, 255) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, ' +
      'rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px',
    '& .MuiMenu-list': {
      padding: '4px 0',
    },
    '& .MuiMenuItem-root': {
      '& .MuiSvgIcon-root': {
        fontSize: 18,
        color: theme.palette.text.secondary,
        marginRight: theme.spacing(1.5),
        ...theme.applyStyles('dark', {
          color: 'inherit',
        }),
      },
      '&:active': {
        backgroundColor: alpha(
          theme.palette.primary.main,
          theme.palette.action.selectedOpacity,
        ),
      },
    },
    ...theme.applyStyles('dark', {
      color: theme.palette.grey[300],
    }),
  },
}));

const SubscriptionStateCell = ({ params, refetchSubscriptions }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedState, setSelectedState] = useState(null);
  const open = Boolean(anchorEl);
  const dispatch = useDispatch();

  const [pause, { isLoading: isPausing }] = usePutPauseStateCodeMutation();
  const [resume, { isLoading: isResuming }] = usePutResumeStateCodeMutation();
  const [activate, { isLoading: isActivating }] =
    usePutActivateStateCodeMutation();
  const [resetInitialization, { isLoading: isResetting }] =
    usePutResetInitializationStateCodeMutation();

  const handleToggle = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => setAnchorEl(null);

  const handleDialogClose = () => setDialogOpen(false);

  const handleDialogConfirm = () => {
    changeSubscriptionState(selectedState);
    setDialogOpen(false);
  };

  const handleMenuItemClick = (jobSubscriptionId, state) => {
    setSelectedState(state);
    setDialogOpen(true);
    handleClose();
  };

  const changeSubscriptionState = async (newState) => {
    switch (newState) {
      case 'Paused':
        try {
          await pause({
            id: params.row.JobSubscriptionId,
          }).unwrap();
          dispatch(
            showSnackbar({
              message: `Subscription paused successfully.`,
              severity: 'success',
            }),
          );
        } catch (error) {
          dispatch(
            showSnackbar({
              message: `Failed to pause subscription: ${error.message}`,
              severity: 'error',
            }),
          );
        }
        break;
      case 'Resume':
        try {
          await resume({
            id: params.row.JobSubscriptionId,
          }).unwrap();
          dispatch(
            showSnackbar({
              message: `Subscription resumed successfully.`,
              severity: 'success',
            }),
          );
        } catch (error) {
          dispatch(
            showSnackbar({
              message: `Failed to resume subscription: ${error.message}`,
              severity: 'error',
            }),
          );
        }
        break;
      case 'InitializationFailed':
        try {
          await resetInitialization({
            id: params.row.JobSubscriptionId,
          }).unwrap();
          dispatch(
            showSnackbar({
              message: `Initialization reset successfully.`,
              severity: 'success',
            }),
          );
        } catch (error) {
          dispatch(
            showSnackbar({
              message: `Failed to reset initialization: ${error.message}`,
              severity: 'error',
            }),
          );
        }
        break;
      case 'Activate':
        try {
          await activate({
            id: params.row.JobSubscriptionId,
          }).unwrap();
          dispatch(
            showSnackbar({
              message: `Subscription activated successfully.`,
              severity: 'success',
            }),
          );
        } catch (error) {
          dispatch(
            showSnackbar({
              message: `Failed to activate subscription: ${error.message}`,
              severity: 'error',
            }),
          );
        }
        break;
      default:
        break;
    }
    refetchSubscriptions();
  };

  const getStateIcon = (state) => {
    switch (state) {
      case 'Active':
        return (
          <PlayCircleFilledRoundedIcon
            fontSize="small"
            sx={{ mr: 1, color: '#1E90FF' }}
          />
        );
      case 'Paused':
        return (
          <PauseCircleOutlineIcon
            fontSize="small"
            sx={{ mr: 1, color: 'gray' }}
          />
        );
      case 'Error':
      case 'InitializationFailed':
        return (
          <ErrorOutlineIcon fontSize="small" sx={{ mr: 1, color: '#FF6347' }} />
        );
      case 'New':
        return (
          <CakeRoundedIcon fontSize="small" sx={{ mr: 1, color: '#800080' }} />
        );
      case 'Pending':
        return (
          <HourglassEmptyRoundedIcon
            fontSize="small"
            sx={{ mr: 1, color: '#A9A9A9' }}
          />
        );
      case 'Ready':
        return (
          <ThumbUpAltRoundedIcon
            fontSize="small"
            sx={{ mr: 1, color: '#32CD32' }}
          />
        );
      default:
        return (
          <HelpOutlineIcon fontSize="small" sx={{ mr: 1, color: '#808080' }} />
        );
    }
  };

  const stateTransitions = {
    New: [],
    Pending: [],
    Active: ['Paused'],
    Paused: ['Resume'],
    Error: [],
    InitializationFailed: ['Reset Initialization'],
    Ready: ['Activate', 'Reset Initialization'],
  };

  const allowedTransitions = stateTransitions[params.row.StateCode] || [];

  return (
    <div onClick={(e) => e.stopPropagation()}>
      <Button
        id="state-button"
        size="small"
        variant="outlined"
        disableElevation
        aria-controls={open ? 'state-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        disabled={
          isPausing || isResuming || isActivating || isResetting || false
        }
        startIcon={
          isPausing || isResuming || isActivating || isResetting ? (
            <CircularProgress size={15} />
          ) : (
            getStateIcon(params.row.StateCode)
          )
        }
        onClick={allowedTransitions.length === 0 ? null : handleToggle}
        endIcon={allowedTransitions.length === 0 ? null : <ArrowDropDownIcon />}
        sx={{
          textTransform: 'none',
          borderColor: 'divider',
          ':hover': { borderColor: 'divider' },
          minWidth: 130,
        }}
      >
        {params.row.StateCode}
      </Button>

      <StyledMenu
        id="state-menu"
        anchorEl={anchorEl}
        open={open}
        p={1}
        onClose={handleClose}
        slotProps={{ list: { 'aria-labelledby': 'state-button' } }}
      >
        {allowedTransitions.includes('Resume') && (
          <MenuItem
            sx={{
              py: '6px',
              px: '12px',
              borderRadius: '5px',
            }}
            onClick={() =>
              handleMenuItemClick(params.row.JobSubscriptionId, 'Resume')
            }
          >
            {getStateIcon('Active')} Resume Subscription
          </MenuItem>
        )}
        {allowedTransitions.includes('Paused') && (
          <MenuItem
            onClick={() =>
              handleMenuItemClick(params.row.JobSubscriptionId, 'Paused')
            }
          >
            <PauseCircleOutlineIcon fontSize="small" sx={{ mr: 1 }} />
            Pause Subscription
          </MenuItem>
        )}
        {allowedTransitions.includes('Activate') && (
          <MenuItem
            onClick={() =>
              handleMenuItemClick(params.row.JobSubscriptionId, 'Activate')
            }
          >
            <PlayCircleFilledRoundedIcon fontSize="small" sx={{ mr: 1 }} />
            Activate
          </MenuItem>
        )}
        {allowedTransitions.length > 1 && <Divider sx={{ my: 0.5 }} />}
        {allowedTransitions.includes('Reset Initialization') && (
          <MenuItem
            onClick={() =>
              handleMenuItemClick(
                params.row.JobSubscriptionId,
                'InitializationFailed',
              )
            }
          >
            <RedoRoundedIcon fontSize="small" sx={{ mr: 1 }} />
            Reset Initialization
          </MenuItem>
        )}
      </StyledMenu>

      <Dialog open={dialogOpen} onClose={handleDialogClose}>
        <DialogTitle>Confirm Action</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Do you confirm the action for {params.row.DisplayName}?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose} variant="text">
            Afbryd
          </Button>
          <Button onClick={handleDialogConfirm} variant="contained">
            Bekræft
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default SubscriptionStateCell;
